import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { retailerAPI } from '../../../utils/api';

const RetailerProducts = () => {
  const { showSuccess, showError } = useNotification();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock_quantity: ''
  });

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await retailerAPI.getProducts();
      if (res.data?.success) {
        setProducts(res.data.data || []);
      } else {
        showError(res.data?.message || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      showError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingProduct
        ? `/api/retailer/product/${editingProduct.id || editingProduct._id}`
        : '/api/retailer/product';
      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        showSuccess(editingProduct ? 'Product updated successfully' : 'Product added successfully');
        setShowAddModal(false);
        setEditingProduct(null);
        setFormData({ name: '', description: '', price: '', stock_quantity: '' });
        fetchProducts();
      } else {
        showError(data.message || 'Failed to save product');
      }
    } catch (error) {
      showError('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      stock_quantity: product.stock_quantity || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const response = await fetch(`/api/retailer/product/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        showSuccess('Product deleted successfully');
        fetchProducts();
      } else {
        showError(data.message || 'Failed to delete product');
      }
    } catch (error) {
      showError('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
        <p className="text-gray-400 mt-2">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">My Products</h1>
        <button
          onClick={() => {
            setEditingProduct(null);
            setFormData({ name: '', description: '', price: '', stock_quantity: '' });
            setShowAddModal(true);
          }}
          className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
        >
          Add Product
        </button>
      </div>

      <div className="space-y-4">
        {products.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-xl">
            <p className="text-gray-400">No products yet</p>
            <p className="text-sm text-gray-500 mt-2">Add your first product to get started</p>
          </div>
        ) : (
          products.map((product) => (
            <div
              key={product.id || product._id}
              className="bg-gray-800 rounded-xl p-6"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-light">{product.name}</h3>
                    {product.is_approved ? (
                      <span className="bg-green-900 text-green-300 px-2 py-1 rounded-full text-xs">
                        Approved
                      </span>
                    ) : (
                      <span className="bg-yellow-900 text-yellow-300 px-2 py-1 rounded-full text-xs">
                        Pending
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mb-3">{product.description || 'No description'}</p>
                  <div className="flex items-center space-x-4">
                    <div>
                      <span className="text-gray-400 text-sm">Price: </span>
                      <span className="text-gold font-medium">₹{product.price}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Stock: </span>
                      <span className="text-light font-medium">{product.stock_quantity || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(product)}
                    className="bg-gray-700 text-light px-3 py-2 rounded-lg text-sm hover:bg-gray-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product.id || product._id)}
                    className="bg-red-900 text-red-300 px-3 py-2 rounded-lg text-sm hover:bg-red-800"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-mobile">
            <h2 className="text-xl font-bold text-gold mb-4">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-400 mb-2">Product Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <div>
                <label className="block text-gray-400 mb-2">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <div>
                <label className="block text-gray-400 mb-2">Price (₹)</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <div>
                <label className="block text-gray-400 mb-2">Stock Quantity</label>
                <input
                  type="number"
                  name="stock_quantity"
                  value={formData.stock_quantity}
                  onChange={handleInputChange}
                  required
                  min="0"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-gold text-dark py-3 rounded-lg font-medium"
                >
                  {editingProduct ? 'Update' : 'Add'} Product
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingProduct(null);
                    setFormData({ name: '', description: '', price: '', stock_quantity: '' });
                  }}
                  className="flex-1 bg-gray-700 text-light py-3 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailerProducts;
